import { useState, useRef, useCallback, useEffect } from 'react';

// mode: 'countup' counts elapsed time from zero; 'countdown' counts down from
// durationMs and fires onExpire once it hits zero.
export function useTimer({ mode = 'countup', durationMs = 0, autoStart = false, onExpire } = {}) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isRunning, setIsRunning] = useState(autoStart);
  const intervalRef = useRef(null);
  const startedAtRef = useRef(null);
  const baseElapsedRef = useRef(0);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const tick = useCallback(() => {
    const next = baseElapsedRef.current + (Date.now() - startedAtRef.current);
    if (mode === 'countdown' && next >= durationMs) {
      setElapsedMs(durationMs);
      setIsRunning(false);
      clearInterval(intervalRef.current);
      onExpireRef.current?.();
      return;
    }
    setElapsedMs(next);
  }, [mode, durationMs]);

  useEffect(() => {
    if (!isRunning) return undefined;
    startedAtRef.current = Date.now();
    intervalRef.current = setInterval(tick, 200);
    return () => clearInterval(intervalRef.current);
  }, [isRunning, tick]);

  const start = useCallback(() => {
    baseElapsedRef.current = elapsedMs;
    setIsRunning(true);
  }, [elapsedMs]);

  const pause = useCallback(() => setIsRunning(false), []);

  const reset = useCallback(() => {
    setIsRunning(false);
    baseElapsedRef.current = 0;
    setElapsedMs(0);
  }, []);

  const remainingMs = mode === 'countdown' ? Math.max(0, durationMs - elapsedMs) : 0;

  return { elapsedMs, remainingMs, isRunning, start, pause, reset };
}
